import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { InputNumberProps } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import type { ReconciliationRow } from '../../../Reports/types/reconciliation'
import {
  ReconciliationPayType,
  type ReconcilePayload
} from '../../../Reports/types/reconciliation'
import {
  combineDateAndTime,
  getPayPreview,
  formatTimeRange
} from '../reconciliationUtils'

export type HourPayMode =
  | 'planned'
  | 'actual'
  | 'none'
  | 'custom_time'
  | 'custom_amount'

export type ShiftPayMode = 'shift_rate' | 'none'

function parsePaidAmountInput(s: string): number {
  return parseFloat(s.replace(',', '.'))
}

export function useReconcilePaidTimeModal(
  open: boolean,
  row: ReconciliationRow,
  onSave: (payload: ReconcilePayload) => void
) {
  const { t } = useTranslation()

  const isShiftRow = row.hasShiftRate || row.payRate?.unit === 'shift'

  const [shiftPayMode, setShiftPayMode] = useState<ShiftPayMode>('shift_rate')
  const [hourPayMode, setHourPayMode] = useState<HourPayMode>('planned')
  const [noPayReason, setNoPayReason] = useState('')
  const [customStartDate, setCustomStartDate] = useState<Dayjs | null>(null)
  const [customStartTime, setCustomStartTime] = useState<Dayjs | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Dayjs | null>(null)
  const [customEndTime, setCustomEndTime] = useState<Dayjs | null>(null)
  const [customPaidAmount, setCustomPaidAmount] = useState<string>('')

  useEffect(() => {
    if (!open) return
    const base = row.actualTime ?? row.plannedTime
    const start = dayjs(base.startTime)
    const end = dayjs(base.endTime)
    setShiftPayMode('shift_rate')
    setHourPayMode('planned')
    setNoPayReason('')
    setCustomStartDate(start.startOf('day'))
    setCustomStartTime(start)
    setCustomEndDate(end.startOf('day'))
    setCustomEndTime(end)
    const preview =
      getPayPreview(ReconciliationPayType.ACTUAL, row, null, null) ??
      getPayPreview(ReconciliationPayType.PLANNED, row, null, null)
    setCustomPaidAmount(preview?.amount ?? '33.08')
  }, [open, row])

  const combinedCustomStart = useMemo(
    () => combineDateAndTime(customStartDate, customStartTime),
    [customStartDate, customStartTime]
  )
  const combinedCustomEnd = useMemo(
    () => combineDateAndTime(customEndDate, customEndTime),
    [customEndDate, customEndTime]
  )

  const customTimePreview = useMemo(() => {
    if (!combinedCustomStart || !combinedCustomEnd) return null
    if (!combinedCustomEnd.isAfter(combinedCustomStart)) return null
    return getPayPreview(
      ReconciliationPayType.CUSTOM,
      row,
      combinedCustomStart,
      combinedCustomEnd
    )
  }, [row, combinedCustomStart, combinedCustomEnd])

  const payAmountDisplay = useMemo(() => {
    if (isShiftRow) {
      if (shiftPayMode === 'none') return '0.00'
      const pr = row.payRate
      if (!pr) return '0.00'
      return parseFloat(pr.amount).toFixed(2)
    }
    if (!row.payRate) return '0.00'
    switch (hourPayMode) {
      case 'planned':
        return (
          getPayPreview(ReconciliationPayType.PLANNED, row, null, null)
            ?.amount ?? '0.00'
        )
      case 'actual':
        return (
          getPayPreview(ReconciliationPayType.ACTUAL, row, null, null)
            ?.amount ?? '0.00'
        )
      case 'none':
        return '0.00'
      case 'custom_time':
        return customTimePreview?.amount ?? '0.00'
      case 'custom_amount': {
        const n = parsePaidAmountInput(customPaidAmount)
        if (Number.isNaN(n)) return '0.00'
        return n.toFixed(2)
      }
      default:
        return '0.00'
    }
  }, [
    isShiftRow,
    shiftPayMode,
    row,
    hourPayMode,
    customTimePreview,
    customPaidAmount
  ])

  const payRateSummaryLabel = useMemo(() => {
    const pr = row.payRate
    if (!pr) return '—'
    if (pr.unit === 'shift') {
      return t('RECONCILIATION_SHIFT_RATE_FIXED_LABEL', { amount: pr.amount })
    }
    return `${pr.amount} ${t('RECONCILIATION_RATE_GBP_HOUR')}`
  }, [row.payRate, t])

  const plannedSub = formatTimeRange(row.plannedTime)
  const actualSub = row.actualTime
    ? formatTimeRange(row.actualTime)
    : t('RECONCILIATION_NO_CLOCK_IN_DATA')

  const canSave = useMemo(() => {
    if (isShiftRow) {
      if (shiftPayMode === 'none') return noPayReason.trim().length > 0
      return true
    }
    if (hourPayMode === 'none') return noPayReason.trim().length > 0
    if (hourPayMode === 'custom_time') {
      return (
        !!combinedCustomStart &&
        !!combinedCustomEnd &&
        combinedCustomEnd.isAfter(combinedCustomStart) &&
        !!customTimePreview
      )
    }
    if (hourPayMode === 'custom_amount') {
      const n = parsePaidAmountInput(customPaidAmount)
      return !Number.isNaN(n) && n >= 0
    }
    if (hourPayMode === 'actual' && !row.actualTime) return false
    return true
  }, [
    isShiftRow,
    shiftPayMode,
    hourPayMode,
    noPayReason,
    combinedCustomStart,
    combinedCustomEnd,
    customTimePreview,
    customPaidAmount,
    row.actualTime
  ])

  const handleSave = () => {
    if (!canSave) return
    let payload: ReconcilePayload
    if (isShiftRow) {
      if (shiftPayMode === 'none') {
        payload = {
          payType: ReconciliationPayType.NONE,
          noPayReason: noPayReason.trim()
        }
      } else {
        payload = { payType: ReconciliationPayType.SHIFT_RATE }
      }
    } else {
      switch (hourPayMode) {
        case 'planned':
          payload = { payType: ReconciliationPayType.PLANNED }
          break
        case 'actual':
          payload = { payType: ReconciliationPayType.ACTUAL }
          break
        case 'none':
          payload = {
            payType: ReconciliationPayType.NONE,
            noPayReason: noPayReason.trim()
          }
          break
        case 'custom_time':
          payload = {
            payType: ReconciliationPayType.CUSTOM,
            customStartTime: combinedCustomStart!.toISOString(),
            customEndTime: combinedCustomEnd!.toISOString()
          }
          break
        case 'custom_amount':
          payload = {
            payType: ReconciliationPayType.CUSTOM,
            customPaidAmount: parsePaidAmountInput(customPaidAmount).toFixed(2)
          }
          break
        default:
          payload = { payType: ReconciliationPayType.PLANNED }
      }
    }
    onSave(payload)
  }

  const gbpNumberProps: InputNumberProps = {
    min: 0,
    step: 0.01,
    style: { width: '100%' },
    addonAfter: 'GBP',
    value:
      customPaidAmount === ''
        ? null
        : Number(parsePaidAmountInput(customPaidAmount)),
    onChange: (v) =>
      setCustomPaidAmount(v === null || v === undefined ? '' : String(v))
  }

  return {
    isShiftRow,
    shiftPayMode,
    setShiftPayMode,
    hourPayMode,
    setHourPayMode,
    noPayReason,
    setNoPayReason,
    customStartDate,
    setCustomStartDate,
    customStartTime,
    setCustomStartTime,
    customEndDate,
    setCustomEndDate,
    customEndTime,
    setCustomEndTime,
    payAmountDisplay,
    payRateSummaryLabel,
    plannedSub,
    actualSub,
    canSave,
    handleSave,
    gbpNumberProps
  }
}
